import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const TOGGLES = [
  ["audits_enabled", "Automatic SEO audits", "Scan the catalogue on a schedule for SEO issues."],
  ["ai_suggestions_enabled", "AI SEO suggestions", "Allow AI to propose titles, descriptions and content."],
  ["auto_safe_fixes", "Automatic safe fixes", "Apply only safe, validated fixes without asking."],
  ["manual_approval_required", "Manual approval required", "Changes wait for admin approval before going live."],
  ["product_seo_generation", "Product SEO generation", "Generate SEO metadata for products."],
  ["sitemap_regeneration", "Sitemap regeneration", "Keep XML sitemaps up to date automatically."],
  ["duplicate_detection", "Duplicate detection", "Flag duplicate titles, descriptions and SKUs."],
  ["schema_validation", "Structured data validation", "Validate JSON-LD / schema on pages."],
  ["notification_emails", "SEO notification emails", "Email a summary of audits and issues."],
  ["protect_manual_edits", "Protect manually edited SEO", "Never let automation overwrite manual edits. Recommended ON."],
];
const FREQ = ["daily", "weekly", "monthly"];

export default function SeoSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/seo/automation").then((r) => setS(r.data)).catch(() => setS(false)); }, []);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/seo/automation", s); setS(r.data); toast.success("Automation settings saved"); }
    catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  if (s === false) return <p className="text-sm text-destructive">Could not load settings.</p>;
  if (!s) return <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>;

  return (
    <div className="max-w-2xl space-y-5" data-testid="seo-settings">
      <div className="rounded-xl border bg-emerald-50/50 p-3 flex items-start gap-2 text-sm text-emerald-800">
        <ShieldCheck size={18} className="shrink-0 mt-0.5" /> Manual SEO edits stay protected by default — automation never overrides a locked or manually edited page unless you turn that protection off here.
      </div>
      <div className="rounded-xl border bg-white divide-y">
        {TOGGLES.map(([k, label, desc]) => (
          <div key={k} className="flex items-center justify-between gap-4 p-4">
            <div><div className="font-semibold text-steel text-sm">{label}</div><div className="text-xs text-muted-foreground">{desc}</div></div>
            <Switch checked={!!s[k]} onCheckedChange={(v) => set(k, v)} data-testid={`toggle-${k}`} />
          </div>
        ))}
        <div className="flex items-center justify-between gap-4 p-4">
          <div><div className="font-semibold text-steel text-sm">Scheduled audit frequency</div><div className="text-xs text-muted-foreground">How often automatic audits run.</div></div>
          <select value={s.audit_frequency} onChange={(e) => set("audit_frequency", e.target.value)} className="h-9 rounded-md border px-3 text-sm bg-white capitalize" data-testid="audit-frequency">
            {FREQ.map((f) => <option key={f} value={f} className="capitalize">{f}</option>)}
          </select>
        </div>
      </div>
      <Button className="bg-accent text-steel hover:bg-amber-600 font-semibold" onClick={save} disabled={saving} data-testid="save-automation">
        {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null} Save settings
      </Button>
    </div>
  );
}
